import { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

export default function Testimonials({ section, items }) {
  const headline = section?.headline || "Don't take our word for it.";
  const subheadline = section?.subheadline || "";

  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[i];
    if (child) {
      track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
    }
    setIdx(i);
  };

  useEffect(() => {
    if (!items || items.length <= 1) return;
    const t = setInterval(() => {
      setIdx((p) => {
        const next = (p + 1) % items.length;
        const track = trackRef.current;
        if (track && track.children[next]) {
          track.scrollTo({ left: track.children[next].offsetLeft - track.offsetLeft, behavior: "smooth" });
        }
        return next;
      });
    }, 7000);
    return () => clearInterval(t);
  }, [items]);

  if (!items || items.length === 0) return null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-charcoal text-ivory py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="reveal flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-gold" />
            <span className="eyebrow text-gold">Reviews</span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2
            data-testid="testimonials-headline"
            className="reveal font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl tracking-tight text-ivory"
          >
            {headline}
          </h2>
          {subheadline ? (
            <p className="reveal mt-4 text-ivory/70 font-light leading-relaxed">
              {subheadline}
            </p>
          ) : null}
        </div>

        <div className="reveal relative">
          <div
            ref={trackRef}
            data-testid="testimonials-track"
            className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory gap-5 md:gap-7 pb-2 -mx-5 px-5"
          >
            {items.map((t, i) => (
              <article
                key={t.id || i}
                data-testid={`testimonial-${i}`}
                className="snap-start shrink-0 w-[88%] sm:w-[60%] md:w-[48%] lg:w-[40%] bg-ivory/[0.04] border border-ivory/10 p-6 md:p-9"
              >
                <div className="flex items-center gap-1 text-gold mb-4">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="font-display text-xl md:text-2xl leading-snug text-ivory/95">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <footer className="mt-6 pt-5 border-t border-ivory/10">
                  <div className="font-display text-lg text-ivory">{t.author}</div>
                  {t.role ? (
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/55 mt-0.5">{t.role}</div>
                  ) : null}
                </footer>
              </article>
            ))}
          </div>

          <div className="flex items-center justify-between mt-8">
            <div className="flex items-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollTo(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`testimonials-dot-${i}`}
                  className={`h-1.5 transition-all ${idx === i ? "w-8 bg-gold" : "w-4 bg-ivory/30"}`}
                />
              ))}
            </div>
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollTo(Math.max(0, idx - 1))}
                aria-label="Previous"
                data-testid="testimonials-prev"
                className="h-10 w-10 border border-ivory/20 inline-flex items-center justify-center hover:bg-ivory hover:text-charcoal transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollTo(Math.min(items.length - 1, idx + 1))}
                aria-label="Next"
                data-testid="testimonials-next"
                className="h-10 w-10 border border-ivory/20 inline-flex items-center justify-center hover:bg-ivory hover:text-charcoal transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
